import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Send, CheckCircle2, MessageSquare, LifeBuoy, Handshake } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import Footer from "@/components/Footer";

const ROLES = [
  { value: "traveler", label: "I'm a traveler" },
  { value: "operator", label: "I'm a tour operator" },
  { value: "other", label: "Something else" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", role: "traveler", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — we'll be in touch soon.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "Contact Carivisor",
    description: "Get in touch with the Carivisor team — travelers and Caribbean tour operators welcome.",
  };

  return (
    <div className="min-h-screen bg-[#060810]">
      <Seo
        title="Contact Carivisor | Caribbean Tours & Excursions Support"
        description="Contact the Carivisor team for help with Caribbean tours and excursions, booking questions, or to list your experiences as a Caribbean tour operator."
        keywords="contact Carivisor, Caribbean tours support, list Caribbean tours, become a Caribbean tour operator, Caribbean excursions help"
        path="/contact"
        jsonLd={jsonLd}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-14">
          <p className="text-xs font-mono-caption uppercase tracking-[0.3em] text-cyan-400 mb-4">Get in touch</p>
          <h1 className="font-heading font-extrabold text-white text-4xl sm:text-5xl leading-tight">
            We'd love to <span className="text-gradient-warm">hear from you</span>
          </h1>
          <p className="text-slate-400 mt-4 max-w-2xl mx-auto">
            Questions about booking a Caribbean experience, or a tour operator who'd like to reach travelers? Send us a
            message and our team will get back to you.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Info cards */}
          <div className="lg:col-span-2 space-y-4">
            {[
              { icon: LifeBuoy, title: "Traveler support", copy: "Help with bookings, changes, check-in codes and loyalty points." },
              { icon: Handshake, title: "List your tours", copy: "Are you a Caribbean guide or operator? Reach travelers worldwide with Carivisor." },
              { icon: MessageSquare, title: "General enquiries", copy: "Partnerships, press, feedback or anything else on your mind." },
            ].map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.title} className="glass rounded-2xl p-6">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#ff5e00] to-[#d900ff] flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-heading font-bold text-white text-lg">{c.title}</h3>
                  <p className="text-sm text-slate-400 mt-1">{c.copy}</p>
                </div>
              );
            })}
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            {sent ? (
              <div className="glass rounded-2xl p-10 text-center h-full flex flex-col items-center justify-center" data-testid="contact-success">
                <CheckCircle2 className="w-14 h-14 text-emerald-400 mb-4" />
                <h2 className="font-heading font-extrabold text-white text-2xl mb-2">Message received!</h2>
                <p className="text-slate-400 max-w-sm">Thanks for reaching out, {form.name.split(" ")[0] || "there"}. Our team will get back to you as soon as possible.</p>
                <Link to="/browse" className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full btn-neon font-semibold">
                  Explore experiences
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="glass rounded-2xl p-6 sm:p-8 space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-mono-caption uppercase tracking-widest text-slate-400 mb-2">Name</label>
                    <input data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} required className="cinput" placeholder="Your name" />
                  </div>
                  <div>
                    <label className="block text-xs font-mono-caption uppercase tracking-widest text-slate-400 mb-2">Email</label>
                    <input data-testid="contact-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="cinput" placeholder="you@email.com" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-mono-caption uppercase tracking-widest text-slate-400 mb-2">I am a…</label>
                  <div className="flex flex-wrap gap-2">
                    {ROLES.map((r) => (
                      <button
                        key={r.value}
                        type="button"
                        data-testid={`contact-role-${r.value}`}
                        onClick={() => set("role", r.value)}
                        className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${form.role === r.value ? "btn-neon" : "glass text-slate-300 hover:text-cyan-400"}`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-mono-caption uppercase tracking-widest text-slate-400 mb-2">Subject</label>
                  <input data-testid="contact-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} className="cinput" placeholder="How can we help?" />
                </div>
                <div>
                  <label className="block text-xs font-mono-caption uppercase tracking-widest text-slate-400 mb-2">Message</label>
                  <textarea data-testid="contact-message" value={form.message} onChange={(e) => set("message", e.target.value)} required rows={5} className="cinput resize-none" placeholder="Tell us a bit more..." />
                </div>
                <button data-testid="contact-submit" disabled={sending} className="w-full py-3 rounded-xl btn-neon font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send message</>}
                </button>
                <style>{`.cinput{width:100%;background:#0B1020;border:1px solid #232A4D;border-radius:0.75rem;padding:0.75rem 1rem;font-size:0.875rem;color:#fff}.cinput::placeholder{color:#64748b}.cinput:focus{outline:none;border-color:rgba(34,211,238,0.6)}`}</style>
              </form>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
